import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { ComparisonTable } from "@/components/content/ComparisonTable";
import { VerdictBox } from "@/components/content/VerdictBox";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";
import { NavLink } from "@/components/NavLink";

const CallTrackingTools = () => {
  const comparisonData = [
    { feature: "CallRail", optionA: "Industry leader", optionB: "$45-$145/mo, full-featured" },
    { feature: "CallTrackingMetrics", optionA: "Enterprise-focused", optionB: "$39-$199/mo, advanced" },
    { feature: "WhatConverts", optionA: "Multi-channel", optionB: "$30-$160/mo, forms + calls" },
    { feature: "Invoca", optionA: "AI-powered", optionB: "Enterprise pricing, ML insights" },
    { feature: "Marchex", optionA: "Conversation intelligence", optionB: "Custom pricing, AI analysis" },
    { feature: "PhoneWagon", optionA: "Small business", optionB: "$25-$75/mo, simple" },
    { feature: "Google Ads Call Tracking", optionA: "Built-in", optionB: "Free with Google Ads" },
    { feature: "Facebook Call Tracking", optionA: "Built-in", optionB: "Free with Meta Ads" },
  ];

  const verdicts = [
    { label: "Best Overall", recommendation: "CallRail" },
    { label: "Best Value", recommendation: "WhatConverts or PhoneWagon" },
    { label: "Best for Lawn Care", recommendation: "CallRail or WhatConverts" },
    { label: "Enterprise", recommendation: "CallTrackingMetrics" },
    { label: "Budget Option", recommendation: "Google Ads built-in" },
  ];

  const faqs = [
    {
      question: "Why do lawn care businesses need call tracking?",
      answer: "Most lawn care leads come via phone. Without call tracking, you can't know which marketing channels generate calls. This blind spot leads to wasted ad spend and missed optimization opportunities."
    },
    {
      question: "How does call tracking work?",
      answer: "Call tracking assigns unique phone numbers to different marketing channels (Google Ads, organic search, Facebook, etc.). When customers call, the system records which source generated the lead."
    },
    {
      question: "What's the best call tracking tool for small lawn care businesses?",
      answer: "PhoneWagon or WhatConverts offer excellent value for smaller operations. CallRail is worth the investment for businesses spending $500+/month on marketing who need detailed analytics."
    },
    {
      question: "How much does call tracking cost?",
      answer: "Basic plans start at $25-$45/month. Mid-tier plans with more features run $75-$145/month. Enterprise solutions with AI and conversation intelligence are $200+/month."
    },
    {
      question: "Do I need call tracking if I only use Google Ads?",
      answer: "Google Ads has built-in call tracking, but dedicated tools offer more features: call recording, lead scoring, CRM integration, and tracking across ALL channels (not just Google)."
    },
    {
      question: "Can call tracking record calls?",
      answer: "Yes, most tools offer call recording. This is valuable for training, quality assurance, and understanding why some leads convert and others don't. Check local recording consent laws."
    },
    {
      question: "What metrics should I track?",
      answer: "Key metrics: calls by source, call duration (longer = more qualified), conversion rate by channel, cost per call, first-time vs. repeat callers, and peak call times."
    },
    {
      question: "Does call tracking affect my SEO or NAP consistency?",
      answer: "Properly implemented, no. Use tracking numbers on your website while maintaining your real number for Google Business Profile and citations. Good tools handle this automatically."
    },
    {
      question: "Can I integrate call tracking with my CRM?",
      answer: "Yes, major call tracking platforms integrate with popular CRMs like HubSpot, Salesforce, Jobber, and Service Autopilot. This creates complete lead records with call data."
    },
    {
      question: "How do I set up call tracking for my lawn care website?",
      answer: "Choose a platform, set up tracking numbers for each channel, install website code for dynamic number insertion, connect your CRM, and configure reporting dashboards."
    },
  ];

  const CANONICAL = "https://www.leafrushmarketing.com/comparisons/call-tracking-tools/";

  return (
    <>
      <SEO 
        title="Call Tracking Tools for Lawn Care Websites"
        description="Compare the best call tracking solutions for lawn care businesses. Know which marketing generates calls and optimize your ad spend."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
      />
      <PageSchema 
        title="Call Tracking Tools for Lawn Care Websites"
        description="Compare the best call tracking solutions for lawn care businesses. Know which marketing generates calls."
        url={CANONICAL}
        faqs={faqs}
      />
      
      <article className="py-16">
        <div className="container px-4 md:px-6">
          <LastUpdated />
          
          <header className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Call Tracking Tools for Lawn Care Websites
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Phone calls are your most valuable leads. Here's how to track which marketing channels generate them—so you can invest in what works.
            </p>
          </header>

          <TopCTA />

          <div className="max-w-4xl mx-auto">
            <VerdictBox verdicts={verdicts} />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Call Tracking Platform Comparison</h2>
              <p className="text-muted-foreground mb-6">
                For lawn care businesses investing in <NavLink to="/growth-add-ons/google-ads/" className="text-primary hover:underline">Google Ads</NavLink> or 
                <NavLink to="/lawn-care-website-seo/" className="text-primary hover:underline"> SEO</NavLink>, 
                call tracking reveals what's actually generating leads:
              </p>
              <ComparisonTable 
                optionAName="Platform"
                optionBName="Details"
                rows={comparisonData}
              />
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Why Call Tracking Matters</h2>
              <p className="text-muted-foreground mb-4">
                Without call tracking, you're guessing which marketing works. Consider:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">?</span>
                  <span>Did that call come from Google Ads or organic search?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">?</span>
                  <span>Which keywords generate phone leads (not just clicks)?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">?</span>
                  <span>Is your <NavLink to="/growth-add-ons/gbp/" className="text-primary hover:underline">GBP optimization</NavLink> generating calls?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">?</span>
                  <span>What's your true cost per lead across channels?</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Key Features to Look For</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span><strong>Dynamic number insertion:</strong> Shows different numbers based on traffic source</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span><strong>Call recording:</strong> Review calls for quality and training</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span><strong>CRM integration:</strong> Connect call data with your lead management</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span><strong>Google Ads integration:</strong> Attribute calls to specific campaigns and keywords</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span><strong>Multi-channel tracking:</strong> Track calls from website, GBP, ads, and offline</span>
                </li>
              </ul>
            </section>

            <MidPageCTA />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Getting Started with Call Tracking</h2>
              <ol className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
                  <span>Start with a <NavLink to="/websites/" className="text-primary hover:underline">professional website</NavLink> that generates calls worth tracking</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
                  <span>Choose a call tracking platform based on your budget and needs</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</span>
                  <span>Set up tracking numbers for each marketing channel</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</span>
                  <span>Connect to your CRM and analytics tools</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">5</span>
                  <span>Review data monthly and shift budget to best-performing channels</span>
                </li>
              </ol>
            </section>

            <FAQSection faqs={faqs} />

            <InternalLinks 
              links={[
                { href: "/comparisons/best-crm-forms-lawn-care/", text: "Best CRM & Forms" },
                { href: "/comparisons/seo-website-vs-ads-first-lawn-care/", text: "SEO vs Google Ads" },
                { href: "/website-fixes/high-traffic-no-calls/", text: "High Traffic, No Calls?" },
                { href: "/growth-add-ons/google-ads/", text: "Google Ads Services" },
              ]}
            />
          </div>
        </div>
      </article>

      <BottomCTA />
      
      <LeadFormSection title="Schedule Your Free Consultation" />
    </>
  );
};

export default CallTrackingTools;
